import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { getAccessToken, getRefreshToken, isTokenExpired, clearAuth } from '../utils/auth';
import { AuthService } from '../services/AuthService';
import { SiteProjectService } from '../services/SiteProjectService';
import logger from '../utils/logger';
import { getCurrentRole, hasRoleAtLeast, UserRole } from '../utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactElement;
  /**
   * 이 라우트에 접근하기 위한 최소 role.
   *   - undefined : 인증된 누구나 (viewer 이상)
   *   - 'editor'  : editor 또는 admin
   *   - 'admin'   : admin 전용
   *
   * 미달인 사용자가 접근하면 /projects 로 redirect.
   * Backend 매트릭스 [permissions.ts] 와 sync 되어야 한다 (UX 가드 + defense-in-depth).
   */
  requireRole?: UserRole;
}

/**
 * Protected Route component that:
 * 1. Redirects to login if user is not authenticated
 * 2. Validates projectId if present in URL params
 * 3. (Optional) Enforces minimum role
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireRole }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [isValidatingProject, setIsValidatingProject] = useState(!!projectId);
  const [isProjectValid, setIsProjectValid] = useState(!projectId); // If no projectId, consider valid
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthValid, setIsAuthValid] = useState(false);

  // Check authentication and token validity
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Access token 이 유효하면 통과. 없거나 만료면 refresh token 으로
        // 재발급 시도 (자동 로그인 복원 / 새로고침 후 세션 유지).
        const accessToken = getAccessToken();
        if (accessToken && !isTokenExpired(accessToken)) {
          setIsAuthValid(true);
          return;
        }

        const refreshToken = getRefreshToken();
        if (refreshToken && !isTokenExpired(refreshToken)) {
          logger.log('[ProtectedRoute] Access token missing/expired, refreshing session');
          const result = await AuthService.refreshToken();
          if (result.success) {
            setIsAuthValid(true);
            return;
          }
        }

        logger.warn('[ProtectedRoute] No valid session, clearing auth');
        clearAuth();
        setIsAuthValid(false);
      } catch (error) {
        logger.error('[ProtectedRoute] Auth check failed:', error);
        clearAuth();
        setIsAuthValid(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();

    // Set up interval to periodically check token expiry
    const interval = setInterval(() => {
      const token = getAccessToken();
      if (token && isTokenExpired(token)) {
        logger.warn('[ProtectedRoute] Token expired during session, attempting refresh');
        AuthService.refreshToken().catch(() => {
          clearAuth();
          window.location.href = '/login';
        });
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Validate project ID if present in URL (must be before early returns)
  useEffect(() => {
    if (!projectId) {
      setIsValidatingProject(false);
      setIsProjectValid(true);
      return;
    }

    // checkAuth() 가 끝나기 전에 이 API 호출이 나가면, access token 이 아직
    // 복구되지 않은 상태라 apiClient 인터셉터가 자체적으로 refresh 를 또 시도한다.
    // checkAuth() 의 refreshToken() 호출과 동시에 두 번 refresh 가 나가면 하나가
    // 실패할 수 있는데, 그 경우 apiClient 가 세션 성공 여부와 무관하게
    // 무조건 /login 으로 강제 이동시켜 버린다 (새로고침 시 정상 세션인데도
    // 로그인 페이지로 튕기는 원인). auth 확인이 끝날 때까지 기다린다.
    if (isCheckingAuth || !isAuthValid) {
      return;
    }

    const validateProject = async () => {
      try {
        const projectIdNum = parseInt(projectId, 10);

        if (isNaN(projectIdNum)) {
          logger.error('[ProtectedRoute] Invalid project ID format:', projectId);
          setIsProjectValid(false);
          return;
        }

        // Call API to verify project exists
        const project = await SiteProjectService.getProject(projectIdNum);

        if (!project) {
          logger.error('[ProtectedRoute] Project not found:', projectId);
          setIsProjectValid(false);
          return;
        }

        logger.log('[ProtectedRoute] Project validated:', { id: project.id, name: project.name });
        setIsProjectValid(true);
      } catch (error) {
        logger.error('[ProtectedRoute] Project validation failed:', {
          projectId,
          error: error instanceof Error ? error.message : String(error)
        });
        setIsProjectValid(false);
      } finally {
        setIsValidatingProject(false);
      }
    };

    validateProject();
  }, [projectId, isCheckingAuth, isAuthValid]);

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Typography variant="h6">인증 확인 중...</Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated or token expired
  if (!isAuthValid) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while validating project
  if (isValidatingProject) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Typography variant="h6">프로젝트 확인 중...</Typography>
      </Box>
    );
  }

  // Redirect if project is invalid
  if (projectId && !isProjectValid) {
    logger.log('[ProtectedRoute] Redirecting to home - invalid project');
    return <Navigate to="/" replace />;
  }

  // Role guard — backend 매트릭스 의 minimum role 미달이면 /projects 로 redirect.
  // backend 가 403 으로도 차단하지만, 잘못된 페이지로 들어가서 빈 화면을 보는
  // UX 를 막기 위해 client 측에서도 한 번 더 확인.
  if (requireRole) {
    const currentRole = getCurrentRole();
    if (!hasRoleAtLeast(currentRole, requireRole)) {
      logger.warn('[ProtectedRoute] Role guard blocked access', {
        path: window.location.pathname,
        currentRole,
        requireRole,
      });
      return <Navigate to="/projects" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
